<template lang="pug">
div
  v-card.neutral300.round-xl.mt-6.pa-6(flat)
    div.text-xl.font-semibold.neutral700--text Sales comparison
    v-row.mt-1
      v-col(cols="12" md="6")
        div.text-md.font-semibold.neutral700--text Previous
        v-card.mt-2.round-xl(flat)
          apexchart(type="bar" height="300" :options="previousChartOptions" :series="previousChartOptions.series")
          v-overlay.primary--text.text-center(v-if="isEmptyPreviousChartData" absolute color="white" opacity="0.9" z-index="2")
            v-icon.mb-2(color="primary" size=20) $yzi-bar-chart-10
            .h8.mb-1 No data available
            .text-sm There’s no data available yet for this chart
      v-col(cols="12" md="6")
        div.text-md.font-semibold.neutral700--text Current
        v-card.mt-2.round-xl(flat)
          apexchart(type="bar" height="300" :options="currentChartOptions" :series="currentChartOptions.series")
          v-overlay.primary--text.text-center(v-if="isEmptyCurrentChartData" absolute color="white" opacity="0.9" z-index="2")
            v-icon.mb-2(color="primary" size=20) $yzi-bar-chart-10
            .h8.mb-1 No data available
            .text-sm There’s no data available yet for this chart
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { rangeValueFromPeriod } from '@/utils'
import { DateTime } from 'luxon'
import axios from 'axios'

export default {
  data () {
    return {
      isFetching: false,
      cancelledRequest: false,
      current: {},
      previous: {},
      currentChartLabels: [],
      currentChartValues: [],
      currentTimeframe: '',
      previousChartLabels: [],
      previousChartValues: [],
      previousTimeframe: '',
      source: null,
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      settings: 'marketing/getMarketingSettings',
      dates: 'marketing/getDates',
      beginDate: 'marketing/getBeginDate',
      untilDate: 'marketing/getUntilDate',
      currentDateRange: 'marketing/getCurrentDateRange'
    }),
    isEmptyCurrentChartData () {
      if (this.isFetching) {
        return true
      }
      return this.currentChartValues.every(item => item === 0)
    },
    isEmptyPreviousChartData () {
      if (this.isFetching) {
        return true
      }
      return this.previousChartValues.every(item => item === 0)
    },
    currentChartOptions () {
      return {
        series: [{
          name: '',
          data: this.currentChartValues
        }],
        colors: ['#007AFF'],
        chart: {
          toolbar: {
            show: false,
          },
        },
        plotOptions: {
          bar: {
            borderRadius: 4,
            borderRadiusApplication: 'end',
          }
        },
        dataLabels: {
          enabled: false
        },
        legend: {
          show: false
        },
        xaxis: {
          categories: this.currentChartLabels,
          labels: {
            formatter: (value) => {
              return this.dateFormatFromTimeFrame(value, false, this.currentTimeframe)
            }
          },
        },
        yaxis: {
          labels: {
            formatter: (value) => {
              return this.$options.filters.displayPriceNoCent(value, this.$currency)
            }
          },
        },
        tooltip: {
          x: {
            formatter: (value) => {
              const date = DateTime.fromISO(value)
              switch (this.currentTimeframe) {
                case 'monthly':
                  return date.toFormat('MMM yyyy')
                case 'hourly':
                  return date.toFormat('h a, d MMM yyyy')
                default:
                  return date.toFormat('d MMM yyyy')
              }
            }
          }
        },
      }
    },
    previousChartOptions () {
      return {
        series: [{
          name: '',
          data: this.previousChartValues
        }],
        colors: ['#007AFF'],
        chart: {
          toolbar: {
            show: false,
          },
        },
        plotOptions: {
          bar: {
            borderRadius: 4,
            borderRadiusApplication: 'end',
          }
        },
        dataLabels: {
          enabled: false
        },
        legend: {
          show: false
        },
        xaxis: {
          categories: this.previousChartLabels,
          labels: {
            formatter: (value) => {
              return this.dateFormatFromTimeFrame(value, false, this.previousTimeframe)
            }
          },
        },
        yaxis: {
          labels: {
            formatter: (value) => {
              return this.$options.filters.displayPriceNoCent(value, this.$currency)
            }
          },
        },
        tooltip: {
          x: {
            formatter: (value) => {
              const date = DateTime.fromISO(value)
              switch (this.previousTimeframe) {
                case 'monthly':
                  return date.toFormat('MMM yyyy')
                case 'hourly':
                  return date.toFormat('h a, d MMM yyyy')
                default:
                  return date.toFormat('d MMM yyyy')
              }
            }
          }
        },
      }
    },
  },
  watch: {
    dates () {
      this.fetchData()
    },
  },
  mounted () {
    this.fetchData()
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchData () {
      this.source = axios.CancelToken.source()
      this.$nextTick(() => {
        this.fetchMarketingSalesComparison()
      })
    },
    fetchMarketingSalesComparison () {
      this.isFetching = true
      let params = {
        begin: this.beginDate,
        until: this.untilDate,
        range: rangeValueFromPeriod(this.currentDateRange?.id)
      }
      this.$axios.get(`/marketing/${this.company.slug}/sales_comparison`, { params, cancelToken: this.source.token })
        .then(resp => {
          let data = resp.data
          this.current = data.current
          this.previous = data.previous
          this.updateChart()
          this.cancelledRequest = false
        })
        .catch(() => {
          this.cancelledRequest = true
        })
        .finally(() => {
          this.isFetching = false
        })
    },
    updateChart () {
      this.currentChartLabels = this.current.points.map(x => x.point)
      this.currentChartValues = this.current.points.map(i => i.data)
      this.currentTimeframe = this.current.timeframe
      this.previousChartLabels = this.previous.points.map(x => x.point)
      this.previousChartValues = this.previous.points.map(i => i.data)
      this.previousTimeframe = this.previous.timeframe
    },
    dateFormatFromTimeFrame (value, showDetail, timeframe) {
      const date = DateTime.fromISO(value)
      switch (timeframe) {
        case 'monthly':
          return date.toFormat('MMM')
        case 'daily':
          return date.toFormat(showDetail ? 'd MMM' : 'd')
        case 'hourly':
          return date.toFormat('h a')
        default:
          return date.toFormat('h')
      }
    },
    cancelRequest () {
      this.source.cancel()
      // console.log('Request has been cancelled')
    },
  }
}
</script>

<style lang="scss" scoped>

</style>